import { ProjectData } from '@/types/project';
import { SavedCalculation } from '@/types/cvr';
import { supabase } from './client';
import { logError } from '@/utils/logger';

export async function fetchCVRCalculations(): Promise<SavedCalculation[]> {
  const { data, error } = await supabase
    .from('cvr_calculations')
    .select('*')
    .order('date_saved', { ascending: false });

  if (error) {
    logError('Error fetching calculations:', error);
    throw new Error('Failed to fetch calculations');
  }

  return data;
}

export async function saveCVRCalculation(
  projectName: string,
  data: ProjectData
): Promise<SavedCalculation> {
  // Validate inputs
  if (!projectName?.trim()) {
    throw new Error('Project name is required');
  }

  if (!data || typeof data !== 'object') {
    throw new Error('Invalid calculation data');
  }

  const {
    data: { user },
  } = await supabase.auth.getUser();

  if (!user) {
    throw new Error('User must be authenticated to save calculations');
  }

  const { data: savedData, error } = await supabase
    .from('cvr_calculations')
    .insert({
      project_name: projectName.trim(),
      data,
      user_id: user.id,
    })
    .select()
    .single();

  if (error) {
    logError('Error saving calculation:', error);
    throw new Error('Failed to save calculation');
  }

  return savedData;
}

export async function renameCVRCalculation(
  id: string,
  newName: string
): Promise<void> {
  const { error } = await supabase
    .from('cvr_calculations')
    .update({ project_name: newName })
    .eq('id', id);

  if (error) {
    logError('Error renaming calculation:', error);
    throw new Error('Failed to rename calculation');
  }
}

export async function deleteCVRCalculation(id: string): Promise<void> {
  const { error } = await supabase
    .from('cvr_calculations')
    .delete()
    .eq('id', id);

  if (error) {
    logError('Error deleting calculation:', error);
    throw new Error('Failed to delete calculation');
  }
}